/**
 * Renders an article's markdown body. Replaces the older ArticleSection
 * component now that the schema has collapsed exec_summary / methodology /
 * findings into a single `body_md` field — section labels (if desired)
 * are just `# Heading` lines in the markdown source.
 *
 * h1/h2/h3 elements get an `id` derived from their text (`slugifyHeading`),
 * which the sidebar TOC links to via `#fragment` URLs. Keep the slug rule
 * here aligned with `extractHeadings()` in headings.ts — they must agree
 * or the TOC links won't scroll anywhere.
 *
 * IMPORTANT: ReactMarkdown is rendered WITHOUT `rehype-raw` and without
 * `skipHtml={false}`. Inline HTML in markdown is escaped — this is
 * required to prevent XSS. The colored callout pattern is provided via
 * `remarkDeltaChip` (a controlled transform), not via raw HTML.
 */

import React from "react";
import { Box } from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import DeltaChip from "./DeltaChip";
import remarkDeltaChip from "./remarkDeltaChip";
import remarkLineBreak from "./remarkLineBreak";
import remarkTableDirectives from "./remarkTableDirectives";
import {
  childrenToHeadingText,
  HEADING_SCROLL_MARGIN_TOP,
  slugifyHeading,
} from "./headings";

interface ArticleBodyProps {
  markdown: string;
}

function HeadingWithAnchor({
  level,
  children,
}: {
  level: 1 | 2 | 3;
  children: React.ReactNode;
}) {
  const id = slugifyHeading(childrenToHeadingText(children));
  // Render a static heading tag with the slug id. The `scroll-margin-top`
  // keeps it from being hidden under the sticky article header chrome
  // when scrolled to via a TOC click. See `HEADING_SCROLL_MARGIN_TOP`
  // for the sizing rationale.
  const Tag = (`h${level}` as unknown) as keyof JSX.IntrinsicElements;
  return (
    <Tag id={id} style={{ scrollMarginTop: HEADING_SCROLL_MARGIN_TOP }}>
      {children}
    </Tag>
  );
}

export default function ArticleBody({ markdown }: ArticleBodyProps) {
  if (!markdown?.trim()) return null;

  return (
    <Box
      component="section"
      sx={{
        // Markdown body typography. All three heading levels are in the
        // same uppercase letter-spaced "section label" family so the
        // body reads like one design language, but they cascade in
        // size / weight / color so the hierarchy is unambiguous:
        //
        //   h1 — top-level section, with an underline divider to anchor it
        //   h2 — subsection inside an h1, no decoration
        //   h3 — minor subdivision, muted color
        "& h1": {
          fontSize: "0.85rem",
          fontWeight: 700,
          letterSpacing: "0.5px",
          textTransform: "uppercase",
          color: "text.primary",
          mt: 4,
          mb: 1.5,
          pb: 0.75,
          borderBottom: "1px solid #e0e0e0",
        },
        "& h2": {
          fontSize: "0.78rem",
          fontWeight: 700,
          letterSpacing: "0.4px",
          textTransform: "uppercase",
          color: "text.primary",
          mt: 3,
          mb: 1,
        },
        "& h3": {
          fontSize: "0.72rem",
          fontWeight: 600,
          letterSpacing: "0.35px",
          textTransform: "uppercase",
          color: "rgba(0, 0, 0, 0.6)",
          mt: 2.25,
          mb: 0.75,
        },
        "& p": { mt: 0.75, mb: 0.75, lineHeight: 1.55 },
        "& ul, & ol": { mt: 0.5, mb: 0.5, pl: 3 },
        "& li": { mb: 0.5, lineHeight: 1.55 },
        "& li > p": { mt: 0, mb: 0 },
        "& code": {
          fontFamily: "Menlo, Monaco, 'Roboto Mono', monospace",
          fontSize: "0.85em",
          backgroundColor: "rgba(0,0,0,0.06)",
          px: 0.5,
          py: 0.1,
          borderRadius: "3px",
        },
        "& pre": {
          backgroundColor: "rgba(0,0,0,0.04)",
          p: 1.5,
          borderRadius: "4px",
          overflowX: "auto",
        },
        "& blockquote": {
          borderLeft: "3px solid #bdbdbd",
          pl: 2,
          ml: 0,
          color: "text.secondary",
        },
        "& table": {
          borderCollapse: "collapse",
          my: 1.5,
          fontSize: "0.9rem",
          "& th, & td": {
            border: "1px solid #e0e0e0",
            px: 1,
            py: 0.5,
            textAlign: "left",
            // Middle-align so multi-line cells (a primary number with a
            // {+x} delta chip stacked below it) sit visually centered
            // against single-line neighbors in the same row.
            verticalAlign: "middle",
          },
          "& th": {
            backgroundColor: "#f5f5f5",
            fontWeight: 600,
          },
          // Mirror header chrome on body cells that the
          // `remarkTableDirectives` plugin tagged as primary-column
          // members (typically the leftmost column when it carries row
          // labels rather than data).
          "& td.primary-col": {
            backgroundColor: "#f5f5f5",
            fontWeight: 600,
          },
        },
        // Whole-table centering opt-in. The plugin tags the `<table>`
        // with this class when a `{table:center}` directive precedes
        // it in source. Auto margins keep it within the article body's
        // intrinsic width rather than fighting the column for space.
        "& table.table-center": {
          marginLeft: "auto",
          marginRight: "auto",
        },
        "& a": {
          color: "#CF0A2C",
          textDecoration: "none",
          "&:hover": { textDecoration: "underline" },
        },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[
          remarkGfm,
          remarkDeltaChip,
          // `remarkLineBreak` converts `<br>` text inside cells (and
          // anywhere else) to real line breaks without enabling
          // `rehype-raw`. `remarkTableDirectives` reads
          // `{table:center,primary=...}` directive lines and tags the
          // following table with classes that the sx rules above style.
          remarkLineBreak,
          remarkTableDirectives,
        ]}
        components={{
          h1: ({ children }) => (
            <HeadingWithAnchor level={1}>{children}</HeadingWithAnchor>
          ),
          h2: ({ children }) => (
            <HeadingWithAnchor level={2}>{children}</HeadingWithAnchor>
          ),
          h3: ({ children }) => (
            <HeadingWithAnchor level={3}>{children}</HeadingWithAnchor>
          ),
          // Route delta-chip spans through DeltaChip; pass other spans through.
          span: ({ className, children, ...props }) => {
            if (typeof className === "string" && className.includes("delta-chip")) {
              return <DeltaChip className={className}>{children}</DeltaChip>;
            }
            return (
              <span className={className} {...props}>
                {children}
              </span>
            );
          },
        }}
      >
        {markdown}
      </ReactMarkdown>
    </Box>
  );
}
